import { css } from 'aphrodite';
import * as React from 'react';
import {
	ISuccessInboxThread,
	SuccessInboxCategory,
	SuccessInboxDisposition,
	useSuccessInboxBulkDispositionMutation,
	useSuccessInboxDispositionMutation,
	useSuccessInboxThreadMessagesQuery,
	useSuccessInboxThreadsQuery,
} from '../../../../queries/Admin/SuccessInbox';
import { Button } from '../../../../web/components-v2/Button';
import { Checkbox } from '../../../../web/components-v2/Checkbox';
import { Pill } from '../../../../web/components-v2/Pill';
import { ThemeColors } from '../../../../web/components-v2/Theme/theme';
import { SuccessInboxEscalateDialog } from '../SuccessInboxEscalateDialog';
import { styleSheet } from './styles';

const POLL_INTERVAL_MS = 60000;

const CATEGORY_COLORS: Record<SuccessInboxCategory, keyof ThemeColors['pill']> = {
	SupportBug: 'red',
	Howto: 'blue',
	Production: 'purple',
	Billing: 'teal',
	Website: 'orange',
	Meeting: 'gray',
	Relationship: 'gray',
	Ads: 'orange',
	Advisory: 'gray',
	NotClient: 'gray',
	Unclassified: 'gray',
};

/** Ordered for the filter bar: what a rep acts on first, then the rest. */
const ALL_CATEGORIES: SuccessInboxCategory[] = [
	'SupportBug',
	'Production',
	'Howto',
	'Billing',
	'Website',
	'Ads',
	'Advisory',
	'Meeting',
	'Relationship',
	'NotClient',
	'Unclassified',
];

const CATEGORY_LABELS: Record<SuccessInboxCategory, string> = {
	SupportBug: 'Product issue',
	Howto: 'How-to',
	Production: 'Do this for me',
	Billing: 'Billing',
	Website: 'Website',
	Meeting: 'Scheduling',
	Relationship: 'FYI',
	Ads: 'Ads',
	Advisory: 'Advice',
	NotClient: 'Not client mail',
	Unclassified: 'Not classified',
};

const WHEN_FORMAT = new Intl.DateTimeFormat('en-US', {
	day: 'numeric',
	hour: 'numeric',
	minute: '2-digit',
	month: 'short',
});

function formatWhen(value: string) {
	const date = new Date(value);
	return Number.isNaN(date.getTime()) ? '—' : WHEN_FORMAT.format(date);
}

const ThreadEmail: React.FC<{ threadId: string }> = ({ threadId }) => {
	const messagesQuery = useSuccessInboxThreadMessagesQuery({ enabled: true, threadId });

	if (messagesQuery.isLoading) {
		return <div className={css(styleSheet.email)}>Reading the mailbox…</div>;
	}

	if (messagesQuery.isError) {
		return <div className={css(styleSheet.email)}>This email could not be read from the mailbox.</div>;
	}

	return (
		<div className={css(styleSheet.email)}>
			{(messagesQuery.data ?? []).map(message => (
				<div className={css(styleSheet.emailMessage)} key={message.providerMessageId}>
					<span className={css(styleSheet.sender)}>
						{message.from} · {formatWhen(message.receivedAt)}
					</span>
					<div className={css(styleSheet.emailBody)}>
						{message.text}
						{message.truncated ? '…' : ''}
					</div>
				</div>
			))}
		</div>
	);
};

interface IThreadRowProps {
	busy: boolean;
	error?: string;
	expanded: boolean;
	onCategoryClick: (category: SuccessInboxCategory) => void;
	onDisposition: (id: string, disposition: SuccessInboxDisposition) => void;
	onEscalate: (thread: ISuccessInboxThread) => void;
	onSelect: (id: string, selected: boolean) => void;
	onToggleExpanded: (id: string) => void;
	selected: boolean;
	thread: ISuccessInboxThread;
}

const ThreadRow: React.FC<IThreadRowProps> = ({
	busy,
	error,
	expanded,
	onCategoryClick,
	onDisposition,
	onEscalate,
	onSelect,
	onToggleExpanded,
	selected,
	thread,
}) => {
	return (
		<div className={css(styleSheet.row, thread.urgent && styleSheet.rowUrgent)}>
			<div className={css(styleSheet.rowBody)}>
				<Checkbox
					aria-label={`Select ${thread.subject || 'thread'}`}
					checked={selected}
					onCheckedChange={checked => onSelect(thread.id, checked === true)}
				/>

				<div className={css(styleSheet.rowMain)}>
					<div className={css(styleSheet.rowHeader)}>
						<span className={css(styleSheet.subject)}>{thread.subject || '(no subject)'}</span>
						<span className={css(styleSheet.sender)}>{formatWhen(thread.lastReceivedAt)}</span>
					</div>

					<span className={css(styleSheet.sender)}>
						{thread.senderName ? `${thread.senderName} <${thread.senderEmail}>` : thread.senderEmail}
						{thread.customerAccountName ? ' · ' : ''}
						{thread.customerAccountName && thread.customerAccountId ? (
							<a
								className={css(styleSheet.accountLink)}
								href={`#/accounts/${thread.customerAccountId}`}
								rel='noopener noreferrer'
								target='_blank'
							>
								{thread.customerAccountName}
							</a>
						) : (
							thread.customerAccountName
						)}
						{thread.messageCount > 1 ? ` · ${thread.messageCount} messages` : ''}
					</span>

					<div className={css(styleSheet.pills)}>
						{thread.urgent ? <Pill color='red' label='Urgent' variant='label' /> : null}
						<button
							aria-label={`Filter to ${CATEGORY_LABELS[thread.category]}`}
							className={css(styleSheet.pillButton)}
							onClick={() => onCategoryClick(thread.category)}
							type='button'
						>
							<Pill
								color={CATEGORY_COLORS[thread.category]}
								label={CATEGORY_LABELS[thread.category]}
								variant='category'
							/>
						</button>
						{thread.autoCcRecommended ? <Pill color='green' label='Support would help' variant='label' /> : null}
						{thread.supportOnThread ? <Pill color='gray' label='Support already on it' variant='label' /> : null}
						{thread.state === 'Resolved' ? <Pill color='gray' label='Looks resolved' variant='label' /> : null}
						{thread.escalatedAt ? <Pill color='green' label='Sent to support' variant='label' /> : null}
					</div>

					{thread.summary || thread.rationale ? (
						<span className={css(styleSheet.rationale)}>{thread.summary || thread.rationale}</span>
					) : null}

					{error ? <div className={css(styleSheet.warning)}>{error}</div> : null}

					{expanded ? <ThreadEmail threadId={thread.id} /> : null}

					<div className={css(styleSheet.actions)}>
						{thread.escalatedAt ? null : (
							<Button disabled={busy} onClick={() => onEscalate(thread)} size='sm' variant='primary'>
								Send to a team
							</Button>
						)}
						<Button disabled={busy} onClick={() => onDisposition(thread.id, 'Resolved')} size='sm' variant='secondary'>
							Resolved
						</Button>
						<Button disabled={busy} onClick={() => onDisposition(thread.id, 'Ignored')} size='sm' variant='ghost'>
							Ignore
						</Button>
						<Button onClick={() => onToggleExpanded(thread.id)} size='sm' variant='ghost'>
							{expanded ? 'Hide email' : 'Read email'}
						</Button>
					</div>
				</div>
			</div>
		</div>
	);
};

export const SuccessInboxThreadList: React.FC<{ userId: string }> = ({ userId }) => {
	const [openOnly, setOpenOnly] = React.useState(true);
	const [categories, setCategories] = React.useState<SuccessInboxCategory[]>([]);
	const [selectedIds, setSelectedIds] = React.useState<string[]>([]);
	const [pendingThreadId, setPendingThreadId] = React.useState<string | null>(null);
	const [errorsByThreadId, setErrorsByThreadId] = React.useState<Record<string, string>>({});
	const [escalating, setEscalating] = React.useState<ISuccessInboxThread | null>(null);
	const [showEverythingElse, setShowEverythingElse] = React.useState(false);
	const [urgentOnly, setUrgentOnly] = React.useState(false);
	const [supportWouldHelp, setSupportWouldHelp] = React.useState(false);
	const [expandedThreadId, setExpandedThreadId] = React.useState<string | null>(null);
	const [bulkError, setBulkError] = React.useState<string | null>(null);

	// A refetch replaces the list, which would throw away a half-typed note or silently change what a
	// checkbox selection refers to. Both are the rep mid-task, so polling waits for them.
	const pollingPaused = escalating !== null || selectedIds.length > 0;

	const threadsQuery = useSuccessInboxThreadsQuery({
		categories,
		openOnly,
		refetchInterval: pollingPaused ? false : POLL_INTERVAL_MS,
		supportWouldHelp,
		urgentOnly,
		userId,
	});

	const clearError = (id: string) =>
		setErrorsByThreadId(current => {
			const next = { ...current };
			delete next[id];
			return next;
		});

	const failThread = (id: string, message: string) => {
		setPendingThreadId(null);
		setErrorsByThreadId(current => ({ ...current, [id]: message }));
	};

	const dispositionMutation = useSuccessInboxDispositionMutation({
		onError: (err, variables) =>
			failThread(variables.id, err?.systemMessage || 'That did not save. The thread has not been changed.'),
		onSuccess: () => setPendingThreadId(null),
	});
	const bulkMutation = useSuccessInboxBulkDispositionMutation({
		onError: err => setBulkError(err?.systemMessage || 'Those threads were not all updated.'),
		onSuccess: () => setSelectedIds([]),
	});

	const threads = threadsQuery.data?.values ?? [];
	const needsAttention = threads.filter(thread => thread.needsAttention);
	const everythingElse = threads.filter(thread => !thread.needsAttention);

	// The second group is collapsed, so the first heading earns its place whenever both exist: it is
	// what tells a rep the short list above is the whole of what needs them.
	const showHeadings = needsAttention.length > 0 && everythingElse.length > 0;

	const handleDisposition = (id: string, disposition: SuccessInboxDisposition) => {
		setPendingThreadId(id);
		clearError(id);
		dispositionMutation.mutate({ disposition, id });
	};

	const handleSelect = (id: string, selected: boolean) =>
		setSelectedIds(current => (selected ? [...current, id] : current.filter(existing => existing !== id)));

	const handleBulk = (disposition: SuccessInboxDisposition) => {
		setBulkError(null);
		bulkMutation.mutate({ disposition, ids: selectedIds });
	};

	const toggleCategory = (category: SuccessInboxCategory) =>
		setCategories(current =>
			current.includes(category) ? current.filter(existing => existing !== category) : [...current, category]
		);

	const renderRow = (thread: ISuccessInboxThread) => (
		<ThreadRow
			busy={pendingThreadId === thread.id}
			error={errorsByThreadId[thread.id]}
			expanded={expandedThreadId === thread.id}
			key={thread.id}
			onCategoryClick={toggleCategory}
			onDisposition={handleDisposition}
			onEscalate={setEscalating}
			onSelect={handleSelect}
			onToggleExpanded={id => setExpandedThreadId(current => (current === id ? null : id))}
			selected={selectedIds.includes(thread.id)}
			thread={thread}
		/>
	);

	if (threadsQuery.isLoading) {
		return <div className={css(styleSheet.empty)}>Loading…</div>;
	}

	if (threadsQuery.isError) {
		return <div className={css(styleSheet.empty)}>Could not load this inbox.</div>;
	}

	return (
		<div className={css(styleSheet.container)}>
			<div className={css(styleSheet.controls)}>
				<Button onClick={() => setOpenOnly(!openOnly)} size='sm' variant='outline'>
					{openOnly ? 'Show everything' : 'Show only what needs attention'}
				</Button>
				<span className={css(styleSheet.message)}>
					{threads.length} thread{threads.length === 1 ? '' : 's'}
				</span>
			</div>

			<div aria-label='Filters' className={css(styleSheet.filterBar)} role='group'>
				<Button onClick={() => setUrgentOnly(!urgentOnly)} size='sm' variant={urgentOnly ? 'primary' : 'outline'}>
					Urgent
				</Button>
				<Button
					onClick={() => setSupportWouldHelp(!supportWouldHelp)}
					size='sm'
					variant={supportWouldHelp ? 'primary' : 'outline'}
				>
					Support would help
				</Button>
				<span className={css(styleSheet.filterDivider)} />
				{ALL_CATEGORIES.map(category => (
					<Button
						key={category}
						onClick={() => toggleCategory(category)}
						size='sm'
						variant={categories.includes(category) ? 'primary' : 'outline'}
					>
						{CATEGORY_LABELS[category]}
					</Button>
				))}
				{urgentOnly || supportWouldHelp || categories.length > 0 ? (
					<Button
						onClick={() => {
							setUrgentOnly(false);
							setSupportWouldHelp(false);
							setCategories([]);
						}}
						size='sm'
						variant='ghost'
					>
						Clear
					</Button>
				) : null}
			</div>

			{selectedIds.length > 0 ? (
				<div className={css(styleSheet.bulkBar)}>
					<span className={css(styleSheet.message)}>{selectedIds.length} selected</span>
					<Button
						disabled={bulkMutation.isPending}
						onClick={() => handleBulk('Resolved')}
						size='sm'
						variant='secondary'
					>
						Mark resolved
					</Button>
					<Button disabled={bulkMutation.isPending} onClick={() => handleBulk('Ignored')} size='sm' variant='ghost'>
						Ignore
					</Button>
					<Button onClick={() => setSelectedIds([])} size='sm' variant='ghost'>
						Clear selection
					</Button>
				</div>
			) : null}

			{bulkError ? <div className={css(styleSheet.warning)}>{bulkError}</div> : null}

			<div aria-label='Threads' className={css(styleSheet.threads)} role='group'>
				{threads.length === 0 ? (
					<div className={css(styleSheet.empty)}>
						{categories.length > 0
							? 'Nothing in those categories. Clear the filters to see the rest.'
							: 'Nothing here. Client email is triaged as it arrives, so this fills in on its own.'}
					</div>
				) : (
					<>
						{showHeadings ? (
							<div className={css(styleSheet.sectionHeading)}>
								<span>Needs attention</span>
								<span className={css(styleSheet.sectionCount)}>{needsAttention.length}</span>
							</div>
						) : null}
						{needsAttention.map(renderRow)}
						{everythingElse.length > 0 ? (
							<>
								<div className={css(styleSheet.sectionHeading, styleSheet.sectionHeadingMuted)}>
									<span>Everything else</span>
									<span className={css(styleSheet.sectionCount)}>{everythingElse.length}</span>
									<Button onClick={() => setShowEverythingElse(!showEverythingElse)} size='sm' variant='ghost'>
										{showEverythingElse ? 'Hide' : 'Show'}
									</Button>
								</div>
								{showEverythingElse ? everythingElse.map(renderRow) : null}
							</>
						) : null}
					</>
				)}
			</div>

			{escalating ? (
				<SuccessInboxEscalateDialog
					onClose={() => setEscalating(null)}
					onSent={() => setEscalating(null)}
					thread={escalating}
				/>
			) : null}
		</div>
	);
};
